import React, { useState, useRef, useEffect } from 'react';
import { Search, Plus, Lock, FileText, MoreHorizontal, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import styles from '../StudyManagement.module.css';
import { checkUserConsent, deleteSubjects } from '../../../Config/api';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import LoadingContainer from '../../../components/Common/LoadingContainer';

const SubjectsTable = ({
  subjects,
  sites,
  loading,
  canWrite,
  canDataCapture,
  onCreateClick,
  onViewSubject,
  onEditSubject,
  onDeleteSubject,
  onLockSubject,
  onRefreshSubjects,
  fetchEnrollmentData,
  fetchingEnrollmentData,
  userAssignedSiteId,
  userRole
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [checkingConsent, setCheckingConsent] = useState(false);
  const [deletingSubject, setDeletingSubject] = useState(null);
  const menuRef = useRef(null);

  // Add safety checks at the top
  const safeSubjects = subjects || [];
  const safeSites = sites || [];

  // Modal state
  const [modal, setModal] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: null,
    onCancel: null
  });

  const showModal = (config) => {
    setModal({
      show: true,
      title: config.title || '',
      message: config.message || '',
      type: config.type || 'info',
      confirmText: config.confirmText || 'Confirm',
      cancelText: config.cancelText || 'Cancel',
      onConfirm: config.onConfirm || closeModal,
      onCancel: config.onCancel || closeModal
    });
  };

  const closeModal = () => {
    setModal({
      show: false,
      title: '',
      message: '',
      type: 'info',
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      onConfirm: null,
      onCancel: null
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const { formatDateTime: formatDateTimeUtil } = require('../../../utils/dateFormatter');
    return formatDateTimeUtil(dateString);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenActionMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Only check consent when CREATING a subject
  const handleCreateSubjectClick = async () => {
    try {
      setCheckingConsent(true);

      const consentData = await checkUserConsent();

      setCheckingConsent(false);

      if (consentData.showConsent === true) {
        const storedUser = JSON.parse(localStorage.getItem('user'));

        showModal({
          title: 'Consent Required',
          message: 'Please fill the consent form first before creating a subject.',
          type: 'warning',
          confirmText: 'Go to Consent Form',
          cancelText: 'Cancel',
          onConfirm: () => {
            closeModal();
            navigate('/service-agreement', {
              state: {
                userData: storedUser,
                returnTo: 'createSubject'
              },
              replace: true
            });
          }
        });
      } else {
        // Fetch enrollment data before opening the drawer
        console.log('Fetching enrollment data for siteId:', userAssignedSiteId);
        console.log('Subjects array:', safeSubjects);

        const enrollmentData = await fetchEnrollmentData(userAssignedSiteId, safeSubjects);

        console.log('Fetched Enrollment Data:', enrollmentData);

        // Pass the fetched enrollment data to the parent
        onCreateClick(enrollmentData);
      }
    } catch (error) {
      setCheckingConsent(false);
      console.error('Error checking consent:', error);

      showModal({
        title: 'Error',
        message: 'Unable to verify consent status. Please try again.',
        type: 'error',
        confirmText: 'OK',
        cancelText: null,
        onConfirm: closeModal,
        onCancel: closeModal
      });
    }
  };

  const handleMenuOpen = (index, event) => {
    if (openActionMenu === index) {
      setOpenActionMenu(null);
    } else {
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      const menuHeight = 120; // Approximate menu height
      const menuWidth = 120;
      const viewportHeight = window.innerHeight;
      
      // Check if menu would go below viewport
      const spaceBelow = viewportHeight - rect.bottom;
      const openUpwards = spaceBelow < menuHeight;

      setMenuPosition({
        top: openUpwards ? rect.top - menuHeight : rect.bottom + 4,
        left: rect.right - menuWidth
      });
      setOpenActionMenu(index);
    }
  };

  // For EDIT - no consent check needed, just open the drawer
  const handleEditSubject = (subject) => {
    onEditSubject({
      ...subject,
      type: 'edit',
      subjectId: subject.subjectId
    });
  };

  const handleDeleteSubject = async (subject) => {
    showModal({
      title: 'Delete Subject',
      message: `Are you sure you want to delete subject ${subject.subjectId}?\n\nThis action cannot be undone.`,
      type: 'delete',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        closeModal();

        try {
          setDeletingSubject(subject.subjectId);
          setOpenActionMenu(null);

          console.log('Deleting subject:', subject.subjectId);

          // Call the delete API with subject ID
          const response = await deleteSubjects(subject.subjectId);

          console.log('Delete response:', response);

          if (response && response.success) {
            // Show success modal
            showModal({
              title: 'Success',
              message: `Subject ${subject.subjectId} has been deleted successfully.`,
              type: 'success',
              confirmText: 'OK',
              cancelText: null,
              onConfirm: async () => {
                closeModal();
                // Refresh subjects list from backend
                if (onRefreshSubjects) {
                  await onRefreshSubjects();
                } else if (onDeleteSubject) {
                  // Fallback to local delete if refresh not available
                  onDeleteSubject(subject.subjectId);
                }
              },
              onCancel: async () => {
                closeModal();
                // Refresh subjects list from backend
                if (onRefreshSubjects) {
                  await onRefreshSubjects();
                } else if (onDeleteSubject) {
                  // Fallback to local delete if refresh not available
                  onDeleteSubject(subject.subjectId);
                }
              }
            });
          } else {
            throw new Error(response?.message || 'Failed to delete subject');
          }

          setDeletingSubject(null);
        } catch (error) {
          setDeletingSubject(null);
          console.error('Error deleting subject:', error);

          const errorMessage = error.response?.data?.message ||
            error.message ||
            'Failed to delete subject. Please try again.';

          showModal({
            title: 'Error',
            message: errorMessage,
            type: 'error',
            confirmText: 'OK',
            cancelText: null,
            onConfirm: closeModal,
            onCancel: closeModal
          });
        }
      }
    });
  };

  const handleAction = (action, subject) => {
    setOpenActionMenu(null);

    if (action === 'View') {
      onViewSubject(subject);
    } else if (action === 'Edit') {
      handleEditSubject(subject);
    } else if (action === 'Delete') {
      handleDeleteSubject(subject);
    } else if (action === 'Lock Subject') {
      onLockSubject(subject.subjectId);
    } else if (action === 'History') {
      console.log('Showing history for:', subject.subjectId);
      // TODO: Implement history functionality
    }
  };

  // Filter subjects based on search term
  const filteredSubjects = safeSubjects.filter((subject) => {
    if (!searchTerm.trim()) return true;

    const searchLower = searchTerm.toLowerCase().trim();

    // Search across all relevant fields
    const siteName = (subject.siteName || '').toLowerCase();
    const subjectNumber = (subject.subjectNumber || '').toLowerCase();
    const subjectInitials = (subject.subjectInitials || '').toLowerCase();
    const enrollmentStatus = (subject.enrollmentStatus || '').toLowerCase();
    const dateOfEnrollment = formatDateTime(subject.dateOfEnrollment).toLowerCase();

    return (
      siteName.includes(searchLower) ||
      subjectNumber.includes(searchLower) ||
      subjectInitials.includes(searchLower) ||
      enrollmentStatus.includes(searchLower) ||
      dateOfEnrollment.includes(searchLower)
    );
  });

  const handleDataCaptureClick = async (subject) => {
    console.log('Starting data capture for subject:', subject);

    try {
      // Determine if user can edit based on role
      // Only PI (Principal Investigator) can edit
      // Sponsor, CRO Admin and Coordinator are view-only
      const canEdit = userRole === 'Principal Investigator';
      
      console.log('User role:', userRole);
      console.log('Can edit:', canEdit);
      
      // Only check consent for PI and Coordinator, skip for CRO Admin and Sponsor
      const needsConsentCheck = ['Principal Investigator', 'Co-ordinator member'].includes(userRole);
      
      if (needsConsentCheck) {
        setCheckingConsent(true);

        const consentData = await checkUserConsent();

        setCheckingConsent(false);

        if (consentData.showConsent === true) {
          const storedUser = JSON.parse(localStorage.getItem('user'));
          console.log('Navigating to consent with user data:', storedUser);

          showModal({
            title: 'Consent Required',
            message: 'Please fill the consent form first before accessing data capture.',
            type: 'warning',
            confirmText: 'Go to Consent Form',
            cancelText: 'Cancel',
            onConfirm: () => {
              closeModal();
              navigate('/service-agreement', {
                state: {
                  userData: storedUser,
                  returnTo: 'dataCapture',
                  subject: subject
                },
                replace: true
              });
            }
          });
          return;
        }
      }

      console.log("Subject data:", subject);

      let studyId = subject.studyId;

      if (!studyId && subject.siteId && safeSites && safeSites.length > 0) {
        const site = safeSites.find(s => s.siteId === subject.siteId);
        if (site) {
          studyId = site.studyId;
        }
      }

      if (!studyId) {
        showModal({
          title: 'Error',
          message: 'Study ID not found for this subject. Please contact support.',
          type: 'error',
          confirmText: 'OK',
          cancelText: null,
          onConfirm: closeModal,
          onCancel: closeModal
        });
        return;
      }

      console.log('Navigating to study:', studyId);
      console.log('Navigation state - isEditMode: true, isViewOnly:', !canEdit);

      // Navigate with isEditMode flag to load submitted data
      // isEditMode: true - loads previously submitted data for editing
      // isViewOnly: false for PI (can edit), true for Sponsor/CRO Admin/Coordinator (read-only)
      navigate(`/survey/${studyId}`, {
        state: {
          subject: {
            subjectId: subject.subjectId,
            siteId: subject.siteId,
            siteName: subject.siteName,
            studyId: studyId
          },
          isEditMode: true, // Always true to load submitted data
          isViewOnly: !canEdit // false for PI (editable), true for Sponsor/CRO Admin/Coordinator (read-only)
        }
      });
    } catch (error) {
      setCheckingConsent(false);
      console.error('Error checking consent:', error);

      showModal({
        title: 'Error',
        message: 'Unable to verify consent status. Please try again.',
        type: 'error',
        confirmText: 'OK',
        cancelText: null,
        onConfirm: closeModal,
        onCancel: closeModal
      });
    }
  };

  return (
    <div className={styles.tableContainer}>
      <ConfirmationModal
        show={modal.show}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={modal.confirmText}
        cancelText={modal.cancelText}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
      />

      <div className={styles.tableHeader}>
        <div className={styles.tableHeaderTop}>
          <h2 className={styles.tableTitle}>Subject Management</h2>
          {canDataCapture && (
            <button
              className={styles.createButton}
              onClick={handleCreateSubjectClick}
              disabled={
                checkingConsent ||
                deletingSubject !== null ||
                fetchingEnrollmentData
              }
            >
              <Plus size={18} />
              {checkingConsent
                ? 'Checking...'
                : fetchingEnrollmentData
                  ? 'Loading...'
                  : 'Create Subject'}
            </button>
          )}
        </div>

        <div className={styles.tableActions}>
          <div className={styles.searchContainer}>
            <Search className={styles.searchIcon} size={18} />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead className={styles.tableHead}>
            <tr>
              <th className={styles.tableHeadCell}>
                Site Name <ChevronDown size={14} className={styles.sortIcon} />
              </th>
              <th className={styles.tableHeadCell}>
                Subject Number <ChevronDown size={14} className={styles.sortIcon} />
              </th>
              <th className={styles.tableHeadCell}>
                Subject Initials <ChevronDown size={14} className={styles.sortIcon} />
              </th>
              <th className={styles.tableHeadCell}>
                Date of Enrollment <ChevronDown size={14} className={styles.sortIcon} />
              </th>
              <th className={styles.tableHeadCell}>
                Enrollment Status <ChevronDown size={14} className={styles.sortIcon} />
              </th>
              {canWrite && (
                <th className={styles.tableHeadCell}>Data Capture</th>
              )}
              {canDataCapture && (<th className={styles.tableHeadCell}>Actions</th>)}
            </tr>
          </thead>
          <tbody className={styles.tableBody}>
            {loading ? (
              <tr>
                <td colSpan={canWrite ? (canDataCapture ? 7 : 6) : 5} className={styles.noDataCell}>
                  <LoadingContainer message="Loading subjects..." />
                </td>
              </tr>
            ) : filteredSubjects.length === 0 ? (
              <tr>
                <td colSpan={canWrite ? (canDataCapture ? 7 : 6) : 5} className={styles.noDataCell}>
                  <div className={styles.noDataMessage}>
                    {searchTerm.trim() ? 'No subjects found matching your search.' : 'No subject data available.'}
                  </div>
                </td>
              </tr>
            ) : (
              filteredSubjects.map((subject, index) => (
                <tr key={subject.subjectId || index} className={styles.tableRow}>
                  <td className={styles.tableCell}>{subject.siteName}</td>
                  <td className={styles.tableCell}>{subject.subjectNumber}</td>
                  <td className={styles.tableCell}>{subject.subjectInitials}</td>
                  <td className={styles.tableCell}>{formatDateTime(subject.dateOfEnrollment)}</td>
                  <td className={styles.tableCell}>
                    <span className={`${styles.statusBadge} ${subject.enrollmentStatus === 'Screening' ? styles.statusScreening : styles.statusEnrolled}`}>
                      {subject.enrollmentStatus}
                    </span>
                  </td>
                  {canWrite && (
                    <td className={styles.tableCellCenter}>
                      {subject.dataCaptureIcon && (
                        <button
                          onClick={() => handleDataCaptureClick(subject)}
                          className={styles.dataCaptureButton}
                          title={
                            userRole === 'Sponsor' 
                              ? "View Form" 
                              : canDataCapture 
                                ? "Data Capture / Edit Form" 
                                : "View Form"
                          }
                          disabled={checkingConsent || deletingSubject !== null}
                        >
                          <FileText size={18} className={styles.icon} />
                        </button>
                      )}
                    </td>
                  )}
                  {canDataCapture && (
                    <td className={styles.tableCellCenter}>
                      <div className={styles.actionMenuWrapper} ref={openActionMenu === index ? menuRef : null}>
                        <button
                          className={styles.moreButton}
                          onClick={(e) => handleMenuOpen(index, e)}
                          disabled={deletingSubject !== null}
                        >
                          <MoreHorizontal size={18} />
                        </button>
                        {openActionMenu === index && (
                          <div 
                            className={styles.actionMenu} 
                            style={{ 
                              top: `${menuPosition.top}px`, 
                              left: `${menuPosition.left}px` 
                            }}
                          >
                            <button className={styles.actionMenuItem} onClick={() => handleAction('View', subject)}>
                              View
                            </button>
                            {canWrite && (
                              <>
                                <button
                                  className={styles.actionMenuItem}
                                  onClick={() => handleAction('Edit', subject)}
                                  disabled={deletingSubject !== null}
                                >
                                  Edit
                                </button>
                                {/* <button className={styles.actionMenuItem} onClick={() => handleAction('History', subject)}>
                                  History
                                </button> */}
                                <button
                                  className={`${styles.actionMenuItem} ${styles.actionMenuItemDanger}`}
                                  onClick={() => handleAction('Delete', subject)}
                                  disabled={deletingSubject !== null}
                                >
                                  {deletingSubject === subject.subjectId ? 'Deleting...' : 'Delete'}
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.pagination}>
        <div className={styles.rowsPerPage}>
          <span className={styles.paginationLabel}>Rows per page:</span>
          <select value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))} className={styles.rowsSelect}>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
        <div className={styles.paginationControls}>
          <span className={styles.pageInfo}>Page 1 of 1</span>
          <div className={styles.paginationButtons}>
            <button className={styles.paginationButton}><ChevronLeft size={18} /></button>
            <button className={styles.paginationButton}><ChevronLeft size={18} /></button>
            <button className={styles.paginationButton}><ChevronRight size={18} /></button>
            <button className={styles.paginationButton}><ChevronRight size={18} /></button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubjectsTable;